import React, { useState, useEffect } from 'react';
import { Sun, Moon } from 'lucide-react';

// Slide data untuk carousel
const SLIDES = [
    { bg: "bg-indigo-600", title: "Minimalist Design", desc: "Clean lines and spaces." },
    { bg: "bg-emerald-600", title: "Efficient Workflow", desc: "Speed up your tasks." },
    { bg: "bg-rose-600", title: "Secure Platform", desc: "Data protection first." },
];

/**
 * Halaman Login dengan carousel animasi
 * @param {function} onLogin - Callback saat login berhasil
 * @param {boolean} isDarkMode - Status dark mode
 * @param {function} toggleTheme - Callback untuk toggle theme
 */
const LoginPage = ({ onLogin, isDarkMode, toggleTheme }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [slideIndex, setSlideIndex] = useState(0);

    // Auto-rotate slides
    useEffect(() => {
        const timer = setInterval(() => {
            setSlideIndex((prev) => (prev + 1) % SLIDES.length);
        }, 4000);
        return () => clearInterval(timer);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await fetch('https://api.cloudnine.biz.id/login.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await res.json();

            if (data.success) {
                // Store token explicitly
                localStorage.setItem('auth_token', data.token);
                // Also store user info if needed
                localStorage.setItem('user_info', JSON.stringify(data.user));
                onLogin();
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (err) {
            console.error(err);
            setError('Connection error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`flex h-screen w-full transition-colors duration-300 ${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'}`}>
            {/* Left Side - Login Form */}
            <div className="w-full md:w-1/2 lg:w-1/3 p-8 flex flex-col justify-center relative">
                {/* Logo */}
                <div className="absolute top-6 left-8 flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-500 rounded flex items-center justify-center text-white font-bold text-xs">C</div>
                    <span className="font-bold text-lg tracking-tight">Cloudnine</span>
                </div>

                {/* Login Form Container */}
                <div className="max-w-xs w-full mx-auto">
                    <h1 className="text-2xl font-semibold mb-1">Welcome back</h1>
                    <p className={`text-xs mb-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Please enter your details to sign in.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Username Input */}
                        <div>
                            <label className="block text-xs font-medium mb-1.5">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className={`w-full text-sm px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${isDarkMode ? 'bg-slate-800 border-slate-700 placeholder-slate-500' : 'bg-white border-slate-200 placeholder-slate-400'}`}
                                placeholder="admin"
                            />
                        </div>

                        {/* Password Input */}
                        <div>
                            <label className="block text-xs font-medium mb-1.5">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={`w-full text-sm px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${isDarkMode ? 'bg-slate-800 border-slate-700 placeholder-slate-500' : 'bg-white border-slate-200 placeholder-slate-400'}`}
                                placeholder="admin"
                            />
                        </div>

                        {/* Error Message */}
                        {error && <p className="text-rose-500 text-xs">{error}</p>}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm py-2 rounded shadow-sm transition-colors"
                        >
                            Sign In
                        </button>
                    </form>

                    {/* Theme Toggle */}
                    <div className="mt-6 flex justify-center">
                        <button
                            onClick={toggleTheme}
                            className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800 text-yellow-400' : 'bg-slate-100 text-slate-600'}`}
                        >
                            {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className={`absolute bottom-6 left-0 w-full text-center text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    © 2024 Cloudnine Admin System
                </div>
            </div>

            {/* Right Side - Carousel */}
            <div className={`hidden md:flex w-1/2 lg:w-2/3 relative overflow-hidden ${isDarkMode ? 'bg-slate-950' : 'bg-slate-100'}`}>
                {SLIDES.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-1000 flex items-center justify-center ${index === slideIndex ? 'opacity-100' : 'opacity-0'}`}
                    >
                        {/* Background overlay */}
                        <div className={`absolute inset-0 ${slide.bg} ${isDarkMode ? 'opacity-20' : 'opacity-10'}`}></div>

                        {/* Decorative blurs */}
                        <div className={`w-96 h-96 rounded-full blur-3xl absolute -top-20 -right-20 ${slide.bg} opacity-20`}></div>
                        <div className={`w-64 h-64 rounded-full blur-2xl absolute bottom-20 left-20 ${slide.bg} opacity-20`}></div>

                        {/* Content */}
                        <div className="z-10 text-center max-w-md p-8">
                            <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                {slide.title}
                            </h2>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                {slide.desc}
                            </p>
                        </div>
                    </div>
                ))}

                {/* Slide Indicators */}
                <div className="absolute bottom-8 left-0 w-full flex justify-center gap-2">
                    {SLIDES.map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-1 rounded-full transition-all duration-300 ${idx === slideIndex
                                ? 'w-6 bg-indigo-500'
                                : `w-2 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-300'}`
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
